import { useState } from 'react';
import { Copy, Check } from 'lucide-react';

interface EmbedCodeSnippetProps {
  propertyId: string;
  theme?: string;
  position?: string;
  primaryColor?: string;
  welcomeMessage?: string;
  enableLeadCapture?: boolean;
  enableImages?: boolean;
}

export function EmbedCodeSnippet({
  propertyId,
  theme = 'auto',
  position = 'bottom-right',
  primaryColor,
  welcomeMessage,
  enableLeadCapture,
  enableImages,
}: EmbedCodeSnippetProps) {
  const [copied, setCopied] = useState(false);

  const widgetBase = import.meta.env.VITE_WIDGET_URL || 'https://cdn.lasoxp.com';
  // Ensure URL ends with the UMD script filename (env may be a bare host)
  const widgetUrl = widgetBase.endsWith('.js') ? widgetBase : `${widgetBase}/lasoxp-widget.umd.js`;
  const apiUrl = import.meta.env.VITE_API_BASE_URL || 'https://api.lasoxp.com';

  const configLines = [
    `    propertyId: "${propertyId}"`,
    `    apiUrl: "${apiUrl}"`,
    `    theme: "${theme}"`,
    `    position: "${position}"`,
  ];
  if (primaryColor) {
    configLines.push(`    primaryColor: "${primaryColor}"`);
  }
  if (welcomeMessage) {
    configLines.push(`    welcomeMessage: "${welcomeMessage.replace(/"/g, '\\"')}"`);
  }
  if (enableLeadCapture !== undefined) {
    configLines.push(`    enableEmailCapture: ${enableLeadCapture}`);
  }
  if (enableImages !== undefined) {
    configLines.push(`    enableImages: ${enableImages}`);
  }

  const code = `<script src="${widgetUrl}"></script>
<script>
  LasoXPWidget.init({
${configLines.join(',\n')}
  });
</script>`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access denied — no-op
    }
  };

  return (
    <div>
      <p className="text-xs text-surface-500 mb-2">
        Add this snippet to your website. The widget automatically loads your saved appearance,
        triggers, and features from the dashboard — the values below are included as overrides.
      </p>
      <div className="relative">
        <pre className="bg-surface-900 text-green-400 text-xs p-4 rounded-lg overflow-x-auto">
          {code}
        </pre>
        <button
          onClick={handleCopy}
          className="absolute top-2 right-2 p-1.5 bg-surface-700 rounded hover:bg-surface-600 text-surface-300"
          title="Copy to clipboard"
        >
          {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}
